import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { vocabularyApi } from '../services/vocabularyApi';
import type { CreateVocabularyTermInput, VocabularyTerm } from '@/modules/core/types/vocabulary.types';

type TermFilters = { field?: string; needs_review?: boolean; is_active?: boolean; search?: string };

// Read hook (useVocabulary) query key — invalidate so curation edits refresh the app's selects.
const READ_KEY = ['inventory', 'vocabulary'] as const;

export const vocabularyTermKeys = {
  all: ['inventory-vocabulary-terms'] as const,
  lists: () => [...vocabularyTermKeys.all, 'list'] as const,
  list: (filters: TermFilters) => [...vocabularyTermKeys.lists(), filters] as const,
};

export function useVocabularyTerms(filters: TermFilters = {}) {
  return useQuery({
    queryKey: vocabularyTermKeys.list(filters),
    queryFn: () => vocabularyApi.listTerms(filters),
  });
}

export function useCreateVocabularyTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateVocabularyTermInput) => vocabularyApi.createTerm(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vocabularyTermKeys.lists() });
      qc.invalidateQueries({ queryKey: READ_KEY });
    },
  });
}

export function useUpdateVocabularyTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<Pick<VocabularyTerm, 'label' | 'aliases' | 'sort_order' | 'is_active'>> }) =>
      vocabularyApi.updateTerm(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vocabularyTermKeys.lists() });
      qc.invalidateQueries({ queryKey: READ_KEY });
    },
  });
}

export function useApproveVocabularyTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => vocabularyApi.approveTerm(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: vocabularyTermKeys.lists() }),
  });
}

export function useDeleteVocabularyTerm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => vocabularyApi.deleteTerm(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vocabularyTermKeys.lists() });
      qc.invalidateQueries({ queryKey: READ_KEY });
    },
  });
}
